import { IPGSample } from '~api/SamplesAPI/IPGSamples';
import { ISample } from '~domain/entities/Sample/ISample';

import { IPGChoiceOfSkus, IPGLoyalty, IPGOfferGroup } from '~api/SamplesAPI/IPGLoyalty';
import { ILoyalty, ILoyaltySKU, ILoyaltyOffer } from '~domain/entities/Loyalty/ILoyalty';

export const parseRawSamples = (samples: IPGSample[]): ISample[] => {
    return samples.map((sample) => {
        const benefits: ISample['benefits'] = {
            ...(sample?.benefits || {}),
            ChoiceOfSkus: {
                ...(sample?.benefits?.ChoiceOfSkus || {}),
                max_allowed: parseInt(sample?.benefits?.ChoiceOfSkus?.max_allowed, 10) || 0,
                pick_per_offer: parseInt(sample?.benefits?.ChoiceOfSkus?.pick_per_offer, 10) || 0,
                use_collection: parseInt(sample?.benefits?.ChoiceOfSkus?.use_collection, 10) || 0,
                display_oos_skus: !!parseInt(sample?.benefits?.ChoiceOfSkus?.display_oos_skus, 10),
                allow_priced: !!parseInt(sample?.benefits?.ChoiceOfSkus?.allow_priced, 10),
                choices: sample?.benefits?.ChoiceOfSkus?.choices,
                sample_group: sample?.benefits?.ChoiceOfSkus?.sample_group,
                max_to_show: parseInt(sample?.benefits?.ChoiceOfSkus?.max_to_show, 10) || 0,
                allow_priced_as_non_sample: !!(
                    parseInt(sample?.benefits?.ChoiceOfSkus?.allow_priced_as_non_sample, 10) || 0
                )
            }
        };

        const messaging: ISample['messaging'] = {
            ...(sample?.messaging || {}),
            forecasted_end_date: new Date(sample?.messaging?.forecasted_end_date),
            forecasted_start_date: new Date(sample?.messaging?.forecasted_start_date),
            critical: !!parseInt(sample?.messaging?.critical, 10),
            high_velocity: !!parseInt(sample?.messaging?.high_velocity, 10)
        };

        return {
            ...(sample || {}),
            benefits,
            messaging,
            modified_date: new Date(sample?.modified_date),
            start_date: new Date(sample?.start_date),
            end_date: new Date(sample?.end_date),
            description: sample?.description
        };
    });
};

export const parseRawLoyalty = (loyalty: IPGLoyalty): ILoyalty => {
    const { offers, user } = loyalty || {};
    const { perks, redeempoints: redeemPoints } = offers || {};

    const { redeemable: perksRedeemable = [], non_redeemable: perksNonRedeemable = [] } =
        perks || {};
    const perksOffers = [...perksRedeemable, ...perksNonRedeemable];

    const {
        redeemable: redeemPointsRedeemable = [],
        non_redeemable: redeemPointsNonRedeemable = []
    } = redeemPoints || {};
    const redeemPointsOffers = [...redeemPointsRedeemable, ...redeemPointsNonRedeemable];

    const {
        pointsAvailable,
        rbTierName: tierName,
        max_offers_allowed: maxPerksAllowed = 1,
        loyaltyMultipleOfferRedeem = false
    } = user || {};

    const allPoints: number[] = [];

    let offersApplied: number = 0;
    let pointsApplied: number = 0;

    const extractChoiceOfSkus = (
        offerGroup: IPGOfferGroup,
        choiceOfSkus: IPGChoiceOfSkus,
        offerCode: string,
        points?: number
    ): ILoyaltySKU => {
        const { product, sku } = choiceOfSkus.choicesPcatData[0];
        const { meets_criteria } = offerGroup;

        const skuDetails = {
            ...product,
            ...sku,
            offer_code: offerCode,
            meetsCriteria: !!meets_criteria
        };

        if (points) {
            return {
                ...skuDetails,
                points
            };
        }

        return skuDetails;
    };

    const extractOfferGroup = (
        offerGroup: IPGOfferGroup,
        offerCode: string,
        points?: number
    ): ILoyaltyOffer => {
        const { image, in_cart, meets_criteria, promo_text_title } = offerGroup;

        const offerDetails = {
            PROD_RGN_NAME: promo_text_title,
            offer_code: offerCode,
            SMALL_IMAGE: image,
            SKU_BASE_ID: offerCode,
            loyaltyOfferApplied: !!in_cart,
            meetsCriteria: !!meets_criteria
        };

        if (points) {
            return {
                ...offerDetails,
                points
            };
        }

        return offerDetails;
    };

    const setPerksOffers = perksOffers.map((offer) => {
        const offerCode = Object.keys(offer)[0];
        const offerGroup = offer[offerCode];
        const choiceOfSkus = offerGroup?.ChoiceOfSkus;

        offersApplied += Number(offerGroup.in_cart);

        if (choiceOfSkus?.choicesPcatData) {
            return extractChoiceOfSkus(offerGroup, choiceOfSkus, offerCode);
        } else {
            return extractOfferGroup(offerGroup, offerCode);
        }
    });

    const setRedeemOffers = redeemPointsOffers.map((offer) => {
        const offerCode = Object.keys(offer)[0];
        const offerGroup = offer[offerCode];
        const choiceOfSkus = offerGroup?.ChoiceOfSkus;
        const points = Number(offerGroup?.DeductLoyaltyPoints?.points) || 0;

        pointsApplied += !!offerGroup.in_cart ? points : 0;

        if (points !== 0) {
            allPoints.push(points);
        }

        if (choiceOfSkus?.choicesPcatData) {
            return extractChoiceOfSkus(offerGroup, choiceOfSkus, offerCode, points);
        } else {
            return extractOfferGroup(offerGroup, offerCode, points);
        }
    });

    return {
        perks: {
            offers: setPerksOffers,
            offersApplied,
            maxAllowed: maxPerksAllowed,
            tierName,
            loyaltyMultipleOfferRedeem
        },
        redeemPoints: {
            offers: setRedeemOffers,
            offersApplied,
            maxAllowed: 1,
            pointsAvailable,
            pointsApplied,
            minimumPointsRequired: Math.min(...allPoints)
        }
    };
};
